/**
 * Phase 8 · C-39 — Turn-around time reports page.
 * Route: `/reports/tat`  (OrgGuard-protected).
 *
 * Three tabs over the same data window:
 *   • By position   — flat table of reportable requisitions with <TatChip />.
 *                     Clicking a row fires a click handler reserved for the
 *                     Timeline Drawer that lands in C-40.
 *   • By individual — per-owner aggregates with inline <Sparkline />.
 *                     Junior recruiters only see their own row (belt-and-
 *                     braces alongside RLS on the requisitions table).
 *   • By hierarchy  — tree table: org → individuals. Team layer materialises
 *                     automatically once a hierarchy table lands (see
 *                     reportsTatService.listByHierarchy).
 *
 * Data: reportsTatService (live queries, no aggregation tables).
 * Visuals: reuses `<PageHeader />`, `<Sparkline />`, `<TatChip />`,
 *          `<TableAvatar />`, and the same tab-strip/date-picker idiom as
 *          TeamActivityPage so nothing in the reports shell looks bespoke.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Users,
  Clock,
  Target,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RechartsTooltip } from 'recharts';

const CHART_COLORS = ['#10B981', '#6EE7B7', '#06B6D4', '#0EA5E9', '#8B5CF6'];
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { queryKeys } from '@core/query/queryKeys';
import {
  reportsTatService,
  type DateRange,
  type HierarchyTatNode,
  type IndividualTatRow,
  type PositionTatRow,
} from '@core/supabase/services/reportsTatService';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { Sparkline } from '@shared/components/ui/Sparkline';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { TatChip } from '@shared/components/ui/TatChip';
import { PositionTimelineDrawer } from './PositionTimelineDrawer';

// ── Constants / persistence ────────────────────────────────────────────────

const LS_RANGE_PRESET = 'ggj.reports.tat.rangePreset';
const LS_TAB = 'ggj.reports.tat.tab';

type RangePreset = DateRange['preset'];
const RANGE_OPTIONS: { key: RangePreset; label: string }[] = [
  { key: '7d',     label: 'Last 7d' },
  { key: '30d',    label: 'Last 30d' },
  { key: '90d',    label: 'Last 90d' },
  { key: 'custom', label: 'Custom' },
];

type TabId = 'position' | 'individual' | 'hierarchy';
const TABS: { id: TabId; label: string }[] = [
  { id: 'position',   label: 'By position' },
  { id: 'individual', label: 'By individual' },
  { id: 'hierarchy',  label: 'By hierarchy' },
];

function readLs<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeLs(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

function fmtShort(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

// ── Pieces ─────────────────────────────────────────────────────────────────

function RangeDropdown({
  value,
  onChange,
}: {
  value: RangePreset;
  onChange: (v: RangePreset) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = RANGE_OPTIONS.find((r) => r.key === value) ?? RANGE_OPTIONS[1];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {current.label}
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-10 z-30 w-36 rounded-[10px] border py-1 shadow-lg vscode-border vscode-bg-elevated"
        >
          {RANGE_OPTIONS.map((opt) => (
            <li
              key={opt.key}
              role="option"
              aria-selected={opt.key === value}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(opt.key);
                setOpen(false);
              }}
              className={[
                'cursor-pointer px-3 py-1.5 text-[13px] transition-colors hover:vscode-bg-hover',
                opt.key === value ? 'vscode-text-primary font-medium' : 'vscode-text-secondary',
              ].join(' ')}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TabStrip({
  tab,
  onChange,
}: {
  tab: TabId;
  onChange: (t: TabId) => void;
}) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const idx = TABS.findIndex((t) => t.id === tab);
    if (e.key === 'ArrowRight') { e.preventDefault(); onChange(TABS[(idx + 1) % TABS.length].id); }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); onChange(TABS[(idx - 1 + TABS.length) % TABS.length].id); }
  };
  return (
    <div role="tablist" aria-label="TAT report views" className="flex flex-wrap gap-1" onKeyDown={onKey}>
      {TABS.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={t.id === tab}
          tabIndex={t.id === tab ? 0 : -1}
          type="button"
          onClick={() => onChange(t.id)}
          className={[
            'inline-flex h-8 items-center rounded-full border px-3.5 text-[12px] font-medium transition-colors',
            t.id === tab
              ? 'border-[#10B981] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
              : 'vscode-border vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary',
          ].join(' ')}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

const TH_BASE =
  'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary select-none';
const TD_BASE =
  'whitespace-nowrap px-3 py-2 text-[13px] vscode-text-primary';

// ── By position ─────────────────────────────────────────────────────────────

function ByPositionTable({
  rows,
  loading,
  error,
  onRowClick,
}: {
  rows: PositionTatRow[];
  loading: boolean;
  error: boolean;
  onRowClick: (id: string) => void;
}) {
  return (
    <div className="min-w-0 overflow-x-auto rounded-[10px] border vscode-border">
      <table className="w-full min-w-[960px] border-collapse text-sm" role="grid">
        <thead>
          <tr className="border-b vscode-border vscode-bg-secondary">
            <th scope="col" className={TH_BASE}>Title</th>
            <th scope="col" className={TH_BASE}>Client</th>
            <th scope="col" className={TH_BASE}>Owner</th>
            <th scope="col" className={TH_BASE}>Opened</th>
            <th scope="col" className={TH_BASE}>Target close</th>
            <th scope="col" className={TH_BASE}>TAT</th>
            <th scope="col" className={`${TH_BASE} text-right`}>Days open</th>
            <th scope="col" className={TH_BASE}>Stage</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={8} className="py-10 text-center text-[13px] vscode-text-tertiary">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Loading positions…
              </span>
            </td></tr>
          ) : error ? (
            <tr><td colSpan={8} className="py-10 text-center text-[13px] text-amber-700 dark:text-amber-300">
              <span className="inline-flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                Could not load TAT data.
              </span>
            </td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={8} className="py-10 text-center text-[13px] vscode-text-tertiary">
              No positions opened or closed in this range.
            </td></tr>
          ) : rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => onRowClick(r.id)}
              className="group cursor-pointer border-b vscode-border transition-colors hover:vscode-bg-hover"
            >
              <td className={`${TD_BASE} font-medium`}>{r.title}</td>
              <td className={`${TD_BASE} vscode-text-secondary`}>{r.clientName || '—'}</td>
              <td className={TD_BASE}>
                <span className="inline-flex items-center gap-1.5">
                  <TableAvatar name={r.ownerName ?? '—'} size={18} />
                  <span className="truncate">{r.ownerName ?? '—'}</span>
                </span>
              </td>
              <td className={`${TD_BASE} vscode-text-secondary tabular-nums`}>{fmtShort(r.openedAt)}</td>
              <td className={`${TD_BASE} vscode-text-secondary tabular-nums`}>{fmtShort(r.targetCloseDate)}</td>
              <td className={TD_BASE}>
                <TatChip targetCloseDate={r.targetCloseDate} closedAt={r.closedAt} />
              </td>
              <td className={`${TD_BASE} text-right tabular-nums`}>{r.tatDaysActual ?? '—'}</td>
              <td className={`${TD_BASE} vscode-text-secondary`}>{r.stageLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── By individual ──────────────────────────────────────────────────────────

function MetricCell({ value, unit }: { value: number; unit?: string }) {
  return (
    <span className="tabular-nums">
      {value}
      {unit ? <span className="ml-0.5 vscode-text-tertiary text-[11px]">{unit}</span> : null}
    </span>
  );
}

function ByIndividualTable({
  rows,
  loading,
  error,
}: {
  rows: IndividualTatRow[];
  loading: boolean;
  error: boolean;
}) {
  return (
    <div className="min-w-0 overflow-x-auto rounded-[10px] border vscode-border">
      <table className="w-full min-w-[880px] border-collapse text-sm" role="grid">
        <thead>
          <tr className="border-b vscode-border vscode-bg-secondary">
            <th scope="col" className={TH_BASE}>Recruiter</th>
            <th scope="col" className={`${TH_BASE} text-right`}>Active</th>
            <th scope="col" className={`${TH_BASE} text-right`}>Avg days open</th>
            <th scope="col" className={`${TH_BASE} text-right`}>Avg TAT</th>
            <th scope="col" className={`${TH_BASE} text-right`}>Breach %</th>
            <th scope="col" className={`${TH_BASE} text-right`}>On-time fill %</th>
            <th scope="col" className={TH_BASE}>7d activity</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} className="py-10 text-center text-[13px] vscode-text-tertiary">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Loading recruiters…
              </span>
            </td></tr>
          ) : error ? (
            <tr><td colSpan={7} className="py-10 text-center text-[13px] text-amber-700 dark:text-amber-300">
              <span className="inline-flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                Could not load TAT data.
              </span>
            </td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={7} className="py-10 text-center text-[13px] vscode-text-tertiary">
              No recruiter activity in this range.
            </td></tr>
          ) : rows.map((r) => (
            <tr key={r.userId} className="border-b vscode-border transition-colors hover:vscode-bg-hover">
              <td className={TD_BASE}>
                <span className="inline-flex items-center gap-2">
                  <TableAvatar name={r.userName} size={22} />
                  <span className="font-medium">{r.userName}</span>
                </span>
              </td>
              <td className={`${TD_BASE} text-right`}><MetricCell value={r.activePositions} /></td>
              <td className={`${TD_BASE} text-right`}><MetricCell value={r.avgDaysOpen} unit="d" /></td>
              <td className={`${TD_BASE} text-right`}><MetricCell value={r.avgTatActual} unit="d" /></td>
              <td className={`${TD_BASE} text-right`}>
                <span className={r.breachRate > 0.25 ? 'text-red-600 dark:text-red-400 tabular-nums font-medium' : 'tabular-nums'}>
                  {pct(r.breachRate)}
                </span>
              </td>
              <td className={`${TD_BASE} text-right`}>
                <span className={r.onTimeFillRate >= 0.75 ? 'text-emerald-600 dark:text-emerald-400 tabular-nums font-medium' : 'tabular-nums'}>
                  {pct(r.onTimeFillRate)}
                </span>
              </td>
              <td className={TD_BASE}>
                <Sparkline values={r.spark7d} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── By hierarchy ───────────────────────────────────────────────────────────

function HierarchyRow({
  node,
  depth,
  expanded,
  onToggle,
}: {
  node: HierarchyTatNode;
  depth: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const childReportsTooltip =
    node.kind === 'team' && hasChildren
      ? `Direct reports: ${node.children!.map((c) => c.label).join(', ')}`
      : undefined;
  return (
    <tr className="border-b vscode-border transition-colors hover:vscode-bg-hover">
      <td className={TD_BASE} style={{ paddingLeft: 12 + depth * 20 }} title={childReportsTooltip}>
        <span className="inline-flex items-center gap-1.5">
          {hasChildren ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={expanded ? 'Collapse' : 'Expand'}
              className="rounded p-0.5 vscode-text-tertiary hover:vscode-text-primary"
            >
              {expanded
                ? <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
            </button>
          ) : (
            <span className="w-4" aria-hidden />
          )}
          {node.kind === 'org' ? (
            <Users className="h-3.5 w-3.5 vscode-text-tertiary" aria-hidden />
          ) : node.kind === 'individual' ? (
            <TableAvatar name={node.label} size={18} />
          ) : null}
          <span className={node.kind === 'org' ? 'font-semibold' : node.kind === 'team' ? 'font-medium' : ''}>
            {node.label}
          </span>
        </span>
      </td>
      <td className={`${TD_BASE} text-right`}><MetricCell value={node.metrics.activePositions} /></td>
      <td className={`${TD_BASE} text-right`}><MetricCell value={node.metrics.avgDaysOpen} unit="d" /></td>
      <td className={`${TD_BASE} text-right`}><MetricCell value={node.metrics.avgTatActual} unit="d" /></td>
      <td className={`${TD_BASE} text-right tabular-nums`}>{pct(node.metrics.breachRate)}</td>
      <td className={`${TD_BASE} text-right tabular-nums`}>{pct(node.metrics.onTimeFillRate)}</td>
    </tr>
  );
}

function ByHierarchyTable({
  root,
  loading,
  error,
}: {
  root: HierarchyTatNode | undefined;
  loading: boolean;
  error: boolean;
}) {
  // Root expanded by default; any sub-trees collapsed until clicked.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const flatten = useCallback(
    (node: HierarchyTatNode, depth: number, out: { node: HierarchyTatNode; depth: number }[]) => {
      out.push({ node, depth });
      const isOpen = depth === 0 || expanded[node.id];
      if (isOpen && node.children) {
        for (const c of node.children) flatten(c, depth + 1, out);
      }
    },
    [expanded],
  );

  const visible = useMemo(() => {
    if (!root) return [];
    const out: { node: HierarchyTatNode; depth: number }[] = [];
    flatten(root, 0, out);
    return out;
  }, [root, flatten]);

  return (
    <div className="min-w-0 overflow-x-auto rounded-[10px] border vscode-border">
      <table className="w-full min-w-[880px] border-collapse text-sm" role="treegrid">
        <thead>
          <tr className="border-b vscode-border vscode-bg-secondary">
            <th scope="col" className={TH_BASE}>Name</th>
            <th scope="col" className={`${TH_BASE} text-right`}>Active</th>
            <th scope="col" className={`${TH_BASE} text-right`}>Avg days open</th>
            <th scope="col" className={`${TH_BASE} text-right`}>Avg TAT</th>
            <th scope="col" className={`${TH_BASE} text-right`}>Breach %</th>
            <th scope="col" className={`${TH_BASE} text-right`}>On-time fill %</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={6} className="py-10 text-center text-[13px] vscode-text-tertiary">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Loading hierarchy…
              </span>
            </td></tr>
          ) : error ? (
            <tr><td colSpan={6} className="py-10 text-center text-[13px] text-amber-700 dark:text-amber-300">
              <span className="inline-flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                Could not load TAT data.
              </span>
            </td></tr>
          ) : !root ? null : visible.map(({ node, depth }) => (
            <HierarchyRow
              key={`${node.kind}-${node.id}-${depth}`}
              node={node}
              depth={depth}
              expanded={Boolean(expanded[node.id])}
              onToggle={() => setExpanded((prev) => ({ ...prev, [node.id]: !prev[node.id] }))}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Summary cards ──────────────────────────────────────────────────────────

function TatPositionSummary({ rows }: { rows: PositionTatRow[] }) {
  const total = rows.length;
  const open = rows.filter((r) => !r.closedAt).length;
  const closed = rows.filter((r) => r.closedAt).length;
  const avgDaysOpen = Math.round(
    rows.reduce((s, r) => s + (r.tatDaysActual ?? 0), 0) / total || 0
  );
  const breached = rows.filter((r) => {
    if (!r.targetCloseDate) return false;
    const target = new Date(r.targetCloseDate);
    const now = r.closedAt ? new Date(r.closedAt) : new Date();
    return now > target;
  }).length;

  const stageDistribution = rows.reduce((acc, r) => {
    const stage = r.stageLabel || 'Unknown';
    acc[stage] = (acc[stage] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const stageChartData = Object.entries(stageDistribution)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  return (
    <div className="mb-5 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <Target className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <p className="text-[10px] vscode-text-tertiary uppercase">Total positions</p>
            <p className="text-2xl font-bold tabular-nums">{total}</p>
          </div>
        </div>
        <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/20">
            <Clock className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <p className="text-[10px] vscode-text-tertiary uppercase">Open</p>
            <p className="text-2xl font-bold tabular-nums">{open}</p>
          </div>
        </div>
        <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/20">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-[10px] vscode-text-tertiary uppercase">Closed</p>
            <p className="text-2xl font-bold tabular-nums">{closed}</p>
          </div>
        </div>
        <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/20">
            <TrendingUp className="h-5 w-5 text-purple-400" />
          </div>
          <div>
            <p className="text-[10px] vscode-text-tertiary uppercase">Avg days open</p>
            <p className="text-2xl font-bold tabular-nums">{avgDaysOpen}<span className="text-sm vscode-text-tertiary ml-1">d</span></p>
          </div>
        </div>
        <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-500/20">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <p className="text-[10px] vscode-text-tertiary uppercase">Breached</p>
            <p className="text-2xl font-bold tabular-nums text-red-400">{breached}</p>
          </div>
        </div>
      </div>

      {/* Stage distribution chart */}
      {stageChartData.length > 0 && (
        <div className="rounded-xl vscode-bg-secondary p-4">
          <p className="text-xs font-semibold vscode-text-primary mb-3">Positions by stage</p>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stageChartData} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 80 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--vscode-text-tertiary)' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'var(--vscode-text-secondary)' }} axisLine={false} tickLine={false} width={75} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', borderRadius: '8px', fontSize: '11px' }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={16}>
                  {stageChartData.map((_, idx) => (
                    <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function TatIndividualSummary({ rows }: { rows: IndividualTatRow[] }) {
  const totalActive = rows.reduce((s, r) => s + r.activePositions, 0);
  const avgDaysOpen = Math.round(rows.reduce((s, r) => s + r.avgDaysOpen, 0) / rows.length || 0);
  const avgBreachRate = rows.reduce((s, r) => s + r.breachRate, 0) / rows.length;
  const avgOnTimeRate = rows.reduce((s, r) => s + r.onTimeFillRate, 0) / rows.length;

  const performanceData = rows
    .slice(0, 6)
    .map((r) => ({
      name: r.userName.split(' ')[0],
      active: r.activePositions,
      avgTat: r.avgTatActual,
    }));

  return (
    <div className="mb-5 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <Users className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <p className="text-[10px] vscode-text-tertiary uppercase">Total active</p>
            <p className="text-2xl font-bold tabular-nums">{totalActive}</p>
          </div>
        </div>
        <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/20">
            <Clock className="h-5 w-5 text-purple-400" />
          </div>
          <div>
            <p className="text-[10px] vscode-text-tertiary uppercase">Avg days open</p>
            <p className="text-2xl font-bold tabular-nums">{avgDaysOpen}<span className="text-sm vscode-text-tertiary ml-1">d</span></p>
          </div>
        </div>
        <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-500/20">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <p className="text-[10px] vscode-text-tertiary uppercase">Avg breach rate</p>
            <p className="text-2xl font-bold tabular-nums">{pct(avgBreachRate)}</p>
          </div>
        </div>
        <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/20">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-[10px] vscode-text-tertiary uppercase">On-time fill</p>
            <p className="text-2xl font-bold tabular-nums text-emerald-400">{pct(avgOnTimeRate)}</p>
          </div>
        </div>
      </div>

      {/* Recruiter performance chart */}
      {performanceData.length > 1 && (
        <div className="rounded-xl vscode-bg-secondary p-4">
          <p className="text-xs font-semibold vscode-text-primary mb-3">Active positions by recruiter</p>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceData} margin={{ top: 0, right: 20, bottom: 0, left: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--vscode-text-tertiary)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--vscode-text-tertiary)' }} axisLine={false} tickLine={false} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', borderRadius: '8px', fontSize: '11px' }}
                />
                <Bar dataKey="active" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function ReportsTatPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const orgId = user?.orgId ?? '';
  const myUserId = user?.orgUserId ?? null;

  // ── Timeline drawer: positionId stored in URL query param ──
  const [searchParams, setSearchParams] = useSearchParams();
  const timelinePositionId = searchParams.get('positionId') ?? null;

  const openTimeline = useCallback((id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('positionId', id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeTimeline = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('positionId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [rangePreset, setRangePreset] = useState<RangePreset>(
    () => readLs<RangePreset>(LS_RANGE_PRESET, '30d'),
  );
  const onRangeChange = useCallback((v: RangePreset) => {
    setRangePreset(v);
    writeLs(LS_RANGE_PRESET, v);
  }, []);

  const [tab, setTab] = useState<TabId>(() => readLs<TabId>(LS_TAB, 'position'));
  useEffect(() => { writeLs(LS_TAB, tab); }, [tab]);

  const range = useMemo<DateRange>(() => ({ preset: rangePreset }), [rangePreset]);

  const byPositionQ = useQuery({
    queryKey: queryKeys.reportsTat.byPosition(orgId, rangePreset),
    queryFn: () => reportsTatService.listByPosition(orgId, range),
    enabled: Boolean(orgId) && tab === 'position',
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const byIndividualQ = useQuery({
    queryKey: queryKeys.reportsTat.byIndividual(orgId, rangePreset),
    queryFn: () => reportsTatService.listByIndividual(orgId, range),
    enabled: Boolean(orgId) && tab === 'individual',
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const byHierarchyQ = useQuery({
    queryKey: queryKeys.reportsTat.byHierarchy(orgId, rangePreset),
    queryFn: () => reportsTatService.listByHierarchy(orgId, range),
    enabled: Boolean(orgId) && tab === 'hierarchy',
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Junior recruiters: defence-in-depth filter (service is expected to also
  // filter via RLS, but we belt-and-brace in the UI so an accidental policy
  // slip can't leak peers' aggregates).
  const individualRows = useMemo(() => {
    const rows = byIndividualQ.data ?? [];
    if (!perms.isJuniorRecruiter || !myUserId) return rows;
    return rows.filter((r) => r.userId === myUserId);
  }, [byIndividualQ.data, perms.isJuniorRecruiter, myUserId]);

  const hierarchyRoot = useMemo(() => {
    const root = byHierarchyQ.data;
    if (!root) return undefined;
    if (!perms.isJuniorRecruiter || !myUserId) return root;
    return {
      ...root,
      children: (root.children ?? []).filter((c) => c.id === myUserId),
    };
  }, [byHierarchyQ.data, perms.isJuniorRecruiter, myUserId]);

  const onPositionRowClick = useCallback((id: string) => {
    openTimeline(id);
  }, [openTimeline]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-6">
      <PageHeader
        title="Turn-around time"
        subtitle="Position TAT across owners and teams"
        action={<RangeDropdown value={rangePreset} onChange={onRangeChange} />}
        breadcrumb={[
          { label: 'Reports', href: '/reports' },
          { label: 'Turn-around time' },
        ]}
        tabs={<TabStrip tab={tab} onChange={setTab} />}
      />

      {!orgId ? (
        <p className="ggj-text-body vscode-text-secondary">No organisation context.</p>
      ) : (
        <>
          {/* Summary metrics cards */}
          {tab === 'position' && !byPositionQ.isLoading && byPositionQ.data && byPositionQ.data.length > 0 && (
            <TatPositionSummary rows={byPositionQ.data} />
          )}
          {tab === 'individual' && !byIndividualQ.isLoading && individualRows.length > 0 && (
            <TatIndividualSummary rows={individualRows} />
          )}

          {tab === 'position' ? (
            <ByPositionTable
              rows={byPositionQ.data ?? []}
              loading={byPositionQ.isLoading}
              error={byPositionQ.isError}
              onRowClick={onPositionRowClick}
            />
          ) : tab === 'individual' ? (
            <ByIndividualTable
              rows={individualRows}
              loading={byIndividualQ.isLoading}
              error={byIndividualQ.isError}
            />
          ) : (
            <ByHierarchyTable
              root={hierarchyRoot}
              loading={byHierarchyQ.isLoading}
              error={byHierarchyQ.isError}
            />
          )}
        </>
      )}

      {/* C-40 — Position timeline drawer */}
      <PositionTimelineDrawer
        positionId={timelinePositionId}
        onClose={closeTimeline}
      />
    </div>
  );
}
